import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adsAdminApi } from '../../api/adsAdminApi';
import { formatCampaignStatus } from './travelManageConstants';
import { createRequestGuard } from './adminPageUtils';
import {
  CAMPAIGN_OPS_ERROR_MESSAGES,
  isValidCampaignIdFormat,
  resolveAdsCampaignOpsError,
} from './adsCampaignOpsErrors';

function isConflictError(err) {
  if (!err) return false;
  if (err.httpStatus === 409 || err.response?.status === 409 || err.status === 409 || err.code === 95903) {
    return true;
  }
  const msg = String(err.message || '').toLowerCase();
  return msg.includes('conflict') || msg.includes('409') || msg.includes('版本');
}

function DetailRow({ label, value, className = '' }) {
  return (
    <div className="product-detail-row">
      <span className="product-detail-row__label">{label}</span>
      <span className={`product-detail-row__value ${className}`.trim()}>{value ?? '—'}</span>
    </div>
  );
}

/**
 * 廣告活動營運：backend 僅支援依 ID 查詢／強制暫停／解除暫停（無列表 API）。
 */
export default function AdsCampaignOpsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialId = searchParams.get('id') || '';
  const [searchId, setSearchId] = useState(initialId);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [conflictError, setConflictError] = useState(false);
  const [hasSearched, setHasSearched] = useState(Boolean(initialId));
  const [confirmSuspendModal, setConfirmSuspendModal] = useState(false);
  const [confirmReleaseModal, setConfirmReleaseModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const requestGuard = useRef(createRequestGuard()).current;

  async function runSearch(rawId) {
    const trimmedId = String(rawId || '').trim();
    if (!trimmedId) {
      setError(CAMPAIGN_OPS_ERROR_MESSAGES.blankId);
      setHasSearched(false);
      setCampaign(null);
      setConflictError(false);
      setMessage('');
      return;
    }
    if (!isValidCampaignIdFormat(trimmedId)) {
      setError(CAMPAIGN_OPS_ERROR_MESSAGES.invalidFormat);
      setHasSearched(false);
      setCampaign(null);
      setConflictError(false);
      setMessage('');
      return;
    }
    const token = requestGuard.next();
    setLoading(true);
    setError('');
    setMessage('');
    setConflictError(false);
    setHasSearched(true);
    try {
      const data = await adsAdminApi.getCampaign(trimmedId);
      if (!requestGuard.isCurrent(token)) return;
      setCampaign(data);
      setSearchParams({ id: trimmedId }, { replace: true });
    } catch (err) {
      if (!requestGuard.isCurrent(token)) return;
      setCampaign(null);
      if (isConflictError(err)) {
        setConflictError(true);
        setError('');
      } else {
        setError(resolveAdsCampaignOpsError(err, 'lookup'));
      }
    } finally {
      if (requestGuard.isCurrent(token)) setLoading(false);
    }
  }

  useEffect(() => {
    if (initialId) {
      runSearch(initialId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSearch(e) {
    if (e) e.preventDefault();
    await runSearch(searchId);
  }

  async function handleRefresh() {
    const id = campaign?.campaignId ? String(campaign.campaignId) : searchId;
    if (campaign?.campaignId) {
      setSearchId(String(campaign.campaignId));
    }
    await runSearch(id);
  }

  async function handleSuspend() {
    if (!campaign || submitting) return;
    setSubmitting(true);
    setError('');
    setMessage('');
    setConflictError(false);
    try {
      const id = String(campaign.campaignId);
      const res = await adsAdminApi.suspendCampaign(id, {
        expectedRowVersion: campaign.rowVersion,
      });
      setCampaign(res);
      setMessage(`廣告活動 #${id} 已成功強制暫停`);
      setConfirmSuspendModal(false);
    } catch (err) {
      if (isConflictError(err)) {
        setConflictError(true);
        setConfirmSuspendModal(false);
        setError('');
      } else {
        // 保留目前 detail；僅顯示安全繁體中文錯誤
        setError(resolveAdsCampaignOpsError(err, 'suspend'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleReleaseSuspension() {
    if (!campaign || submitting) return;
    setSubmitting(true);
    setError('');
    setMessage('');
    setConflictError(false);
    try {
      const id = String(campaign.campaignId);
      const res = await adsAdminApi.releaseCampaignSuspension(id, {
        expectedRowVersion: campaign.rowVersion,
      });
      setCampaign(res);
      setMessage(`廣告活動 #${id} 已成功解除暫停（狀態為 PAUSED）`);
      setConfirmReleaseModal(false);
    } catch (err) {
      if (isConflictError(err)) {
        setConflictError(true);
        setConfirmReleaseModal(false);
        setError('');
      } else {
        // 保留目前 detail；僅顯示安全繁體中文錯誤
        setError(resolveAdsCampaignOpsError(err, 'release'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  const canSuspend = campaign && ['READY', 'ACTIVE', 'PAUSED'].includes(campaign.status);
  const canRelease = campaign && campaign.status === 'SUSPENDED';

  return (
    <div className="page-card" data-testid="ads-campaigns-page">
      <div className="section-header">
        <h2>廣告活動營運</h2>
        <p style={{ margin: '4px 0 0', color: '#666', fontSize: '13px' }}>
          依活動 ID 查詢後可強制暫停或解除暫停。系統目前不提供活動列表。
        </p>
      </div>

      <form
        className="filter-bar"
        onSubmit={handleSearch}
        style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginBottom: '20px' }}
      >
        <div className="form-field" style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="campaignSearchInput">活動 ID</label>
          <input
            id="campaignSearchInput"
            type="text"
            className="form-control"
            placeholder="請輸入廣告活動 ID"
            value={searchId}
            onChange={(e) => {
              const next = e.target.value;
              setSearchId(next);
              // 修改 ID 時清除過時錯誤／成功回饋；若與已載入 detail 不同則清掉舊 detail
              setError('');
              setConflictError(false);
              setMessage('');
              const trimmed = String(next || '').trim();
              if (campaign && String(campaign.campaignId) !== trimmed) {
                setCampaign(null);
                setHasSearched(false);
              }
            }}
            data-testid="campaign-search-input"
            style={{ width: '280px' }}
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={loading} data-testid="campaign-search-btn">
          {loading ? '查詢中…' : '查詢'}
        </button>
        {campaign ? (
          <button type="button" className="btn btn-secondary" disabled={loading} onClick={handleRefresh} data-testid="campaign-refresh-btn">
            重新整理
          </button>
        ) : null}
      </form>

      {conflictError ? (
        <div
          className="alert alert-warning alert-conflict"
          role="alert"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}
          data-testid="conflict-alert"
        >
          <span>{CAMPAIGN_OPS_ERROR_MESSAGES.http409}</span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={handleRefresh}>
            重新查詢
          </button>
        </div>
      ) : null}

      {error ? (
        <div className="alert alert-danger" role="alert" style={{ marginBottom: '16px' }} data-testid="error-alert">
          {error}
          {hasSearched ? (
            <button type="button" className="btn btn-secondary btn-sm" style={{ marginLeft: 8 }} onClick={handleRefresh} data-testid="campaign-retry-btn">
              重試
            </button>
          ) : null}
        </div>
      ) : null}
      {message ? (
        <div className="alert alert-success" role="alert" style={{ marginBottom: '16px' }} data-testid="success-alert">
          {message}
        </div>
      ) : null}

      {!loading && !campaign && !hasSearched ? (
        <div className="empty-state" style={{ textAlign: 'center', padding: '48px 16px', color: '#666' }} data-testid="campaign-idle-empty">
          目前沒有廣告活動
          <div style={{ marginTop: 8, fontSize: 13 }}>請輸入活動 ID 開始查詢</div>
        </div>
      ) : null}

      {!loading && !campaign && hasSearched && !error ? (
        <div className="empty-state" style={{ textAlign: 'center', padding: '48px 16px', color: '#666' }} data-testid="campaign-not-found-empty">
          目前沒有廣告活動
        </div>
      ) : null}

      {campaign ? (
        <div
          className="campaign-detail-card"
          style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '20px', backgroundColor: '#fff' }}
          data-testid="campaign-detail-card"
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #edf2f7',
              paddingBottom: '12px',
              marginBottom: '16px',
            }}
          >
            <div>
              <h3 style={{ margin: 0, fontSize: '18px' }} data-testid="campaign-id-header">
                活動 #{String(campaign.campaignId)}
              </h3>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span
                className={`badge badge-${campaign.status === 'ACTIVE' ? 'success' : campaign.status === 'SUSPENDED' ? 'danger' : campaign.status === 'PAUSED' ? 'warning' : 'info'}`}
                style={{ fontSize: '14px', padding: '6px 12px' }}
                data-testid="campaign-status-badge"
              >
                {formatCampaignStatus(campaign.status)}
              </span>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                disabled={!canSuspend || submitting}
                onClick={() => setConfirmSuspendModal(true)}
                data-testid="suspend-btn"
              >
                強制暫停
              </button>
              <button
                type="button"
                className="btn btn-warning btn-sm"
                disabled={!canRelease || submitting}
                onClick={() => setConfirmReleaseModal(true)}
                data-testid="release-btn"
              >
                解除暫停
              </button>
            </div>
          </div>

          <div className="product-detail-grid">
            <DetailRow label="活動 ID" value={String(campaign.campaignId)} />
            <DetailRow label="廣告主會員 ID" value={String(campaign.advertiserMemberId ?? '—')} />
            <DetailRow label="關聯推廣申請 ID" value={String(campaign.promotionApplicationId ?? '—')} />
            <DetailRow label="關聯素材 ID" value={String(campaign.creativeId ?? '—')} />
            <DetailRow label="旅程標的 ID" value={String(campaign.sourceContentId ?? '—')} />
            <DetailRow label="標的類型" value={campaign.sourceContentType} />
            <DetailRow
              label="活動排期"
              value={`${campaign.startAt ? String(campaign.startAt).slice(0, 10) : '—'} 至 ${campaign.endAt ? String(campaign.endAt).slice(0, 10) : '—'}`}
            />
            <DetailRow
              label="預算金額"
              value={`${campaign.budgetAmount ?? '—'} ${campaign.budgetCurrency || ''}`.trim()}
            />
            <DetailRow label="每人每日曝光上限" value={campaign.maxImpressionsPerMemberPerDay ?? '無限制'} />
            <DetailRow label="每人每連線曝光上限" value={campaign.maxImpressionsPerMemberPerSession ?? '無限制'} />
            <DetailRow label="最小曝光間隔(分)" value={campaign.minMinutesBetweenImpressions ?? '無限制'} />
            <DetailRow label="定向模式" value={campaign.targetingMode || '—'} />
            <DetailRow
              label="定向地點 IDs"
              value={Array.isArray(campaign.targetLocationIds) && campaign.targetLocationIds.length > 0 ? campaign.targetLocationIds.join(', ') : '全區 (無限定)'}
            />
            <DetailRow label="資料版本" value={campaign.rowVersion} />
            <DetailRow label="啟用時間" value={campaign.activatedAt ? String(campaign.activatedAt).replace('T', ' ').slice(0, 19) : '—'} />
            <DetailRow label="會員暫停時間" value={campaign.pausedAt ? String(campaign.pausedAt).replace('T', ' ').slice(0, 19) : '—'} />
            <DetailRow
              label="強制暫停時間"
              value={campaign.suspendedAt ? String(campaign.suspendedAt).replace('T', ' ').slice(0, 19) : '—'}
              className={campaign.suspendedAt ? 'text-danger' : ''}
            />
            <DetailRow label="終止時間" value={campaign.cancelledAt ? String(campaign.cancelledAt).replace('T', ' ').slice(0, 19) : '—'} />
          </div>
        </div>
      ) : null}

      {confirmSuspendModal ? (
        <div className="admin-modal-overlay" role="presentation" onClick={() => !submitting && setConfirmSuspendModal(false)}>
          <div
            className="admin-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            data-testid="suspend-modal"
          >
            <div className="admin-modal__header">
              <h3>確認強制暫停活動</h3>
              <button type="button" className="admin-modal__close" onClick={() => !submitting && setConfirmSuspendModal(false)}>
                ×
              </button>
            </div>
            <div className="admin-modal__body">
              <p style={{ color: '#c53030', fontWeight: 'bold' }}>
                警告：強制暫停後，此廣告活動將立即停止投放！
              </p>
              <p style={{ color: '#666', fontSize: '13px' }}>
                活動編號：#{String(campaign.campaignId)}
                <br />
                當前版本：{campaign.rowVersion}
              </p>
            </div>
            <div className="admin-modal__footer">
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmSuspendModal(false)} disabled={submitting}>
                取消
              </button>
              <button type="button" className="btn btn-danger" onClick={handleSuspend} disabled={submitting} data-testid="confirm-suspend-btn">
                {submitting ? '處置中…' : '確認強制暫停'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmReleaseModal ? (
        <div className="admin-modal-overlay" role="presentation" onClick={() => !submitting && setConfirmReleaseModal(false)}>
          <div
            className="admin-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            data-testid="release-modal"
          >
            <div className="admin-modal__header">
              <h3>確認解除活動暫停</h3>
              <button type="button" className="admin-modal__close" onClick={() => !submitting && setConfirmReleaseModal(false)}>
                ×
              </button>
            </div>
            <div className="admin-modal__body">
              <p>
                解除強制暫停後，活動會進入 <strong>已暫停</strong> 狀態，不會立即恢復曝光。
              </p>
              <p style={{ color: '#4a5568', fontSize: '13px' }}>
                需由廣告主確認後自行重新啟動投放。
              </p>
              <p style={{ color: '#666', fontSize: '13px' }}>
                活動編號：#{String(campaign.campaignId)}
                <br />
                當前版本：{campaign.rowVersion}
              </p>
            </div>
            <div className="admin-modal__footer">
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmReleaseModal(false)} disabled={submitting}>
                取消
              </button>
              <button type="button" className="btn btn-warning" onClick={handleReleaseSuspension} disabled={submitting} data-testid="confirm-release-btn">
                {submitting ? '處置中…' : '確認解除暫停'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
